import type { PersonDTO } from '@almanac/shared';
import { api } from '../lib/api.js';
import { personInitials } from '../lib/people.js';

/**
 * The avatar circle next to a person's name — their photo when one is
 * set, initials otherwise (iOS-contacts style). The host controls the
 * size/shape via the class it passes (People-row circle, person-card
 * big circle, settings row); this component only decides photo vs
 * initials.
 *
 * The img URL is versioned by `avatarVersion`, which changes only when
 * the photo itself is replaced — the response is immutable, cached by
 * the browser AND the service worker (see vite.config runtimeCaching),
 * so after first load avatars render instantly on every page. Eager +
 * async decoding: these are tiny (~512px JPEGs) and above the fold in
 * short lists, so lazy-loading only added visible pop-in.
 */
export function PersonAvatar({ person, className }: { person: PersonDTO; className: string }) {
  if (person.avatarVersion) {
    return (
      <span className={`${className} person-avatar--photo`} aria-hidden>
        <img
          className="person-avatar__img"
          src={api.personAvatarUrl(person.id, person.avatarVersion)}
          alt=""
          decoding="async"
        />
      </span>
    );
  }
  return (
    <span className={className} aria-hidden>
      {personInitials(person.displayName)}
    </span>
  );
}

/**
 * Warm the avatar caches: fire an off-DOM fetch for every photo the
 * moment the people list is known, so the first visit to any page —
 * People, Documents' by-person folders, a picker — paints avatars from
 * cache instead of racing the network per page. Cheap at household
 * scale, and every request lands in the SW's CacheFirst bucket anyway.
 */
export function prefetchAvatars(people: PersonDTO[] | undefined): void {
  for (const p of people ?? []) {
    if (!p.avatarVersion) continue;
    const img = new Image();
    img.decoding = 'async';
    img.src = api.personAvatarUrl(p.id, p.avatarVersion);
  }
}
